interface ResponseMessage {
    payload: ResponseMessagePayload;
}
interface ResponseMessagePayload {
    initialProjectFiles: initialProjectFile[];
    instructions: string;
    taskList: Task[];
}
interface initialProjectFile {
    filepath: string;
    code: string;
}
export interface Task {
    filepath: string;
    instruction: string;
    code: string;
}

export interface CurrentSource {
    payload?: SourcePayload;
}
export interface SourcePayload {
    files?: Record<string, string>;
}
const tryToCompleteJSON = (partialJSON: string) => {
    // payload: {
    // "instructions": "string"
    //   "taskList": [
    //       {
    //           "filepath": "string",
    //           "code": "string"
    //       }
    //   ],
    //
    // }
    try {
        const jsonStart = partialJSON.indexOf('{');
        if (jsonStart === -1) return undefined;

        partialJSON = partialJSON.substring(jsonStart);

        const strategies = [
            '"}', // Complete a string property
            '""}', // Complete an empty string property
            '"}]}}', // Complete string, array, and two objects
            '""}]}}', // Complete empty string, array, and two objects
            ']}}', // Complete array and two objects
            '}}', // Complete two objects
            '}', // Complete one object
        ];

        for (const completion of strategies) {
            try {
                const attemptJSON = partialJSON + completion;
                return JSON.parse(attemptJSON);
            } catch {
                // Try next strategy
                continue;
            }
        }

        return undefined;
    } catch {
        return undefined;
    }
};
function tryToCompleteFinalJSON(partialJSON: string) {
    // payload: {
    //   "taskList": [
    //       {
    //           "filepath": "string",
    //           "code": "string"
    //       }
    //   ],
    //   "instructions": "string"
    // }
    try {
        const jsonStart = partialJSON.indexOf('{');
        const jsonEnd = partialJSON.lastIndexOf('}');
        if (jsonStart === -1 || jsonEnd === -1) {
            return undefined;
        }

        const jsonStr = partialJSON.substring(jsonStart, jsonEnd + 1);

        return JSON.parse(jsonStr);
    } catch {
        return undefined;
    }
}

const getCodeFiles = (files: Record<string, { code: string }>): SourcePayload => {
    if (!files) return {};
    const nextFiles: Record<string, string> = {};
    for (const [key, value] of Object.entries(files)) {
        nextFiles[key] = value.code;
    }
    return {
        files: nextFiles,
    };
};

const responseMessageParser = (parsedMessage: ResponseMessage) => {
    if (parsedMessage && parsedMessage.payload) {
        let nextMessages: string[] = [];
        const initialProjectFiles = parsedMessage.payload.instructions ? parsedMessage.payload.initialProjectFiles : [];
        const progressiveSource: Record<string, { code: string }> = {};
        try {
            for (const file of initialProjectFiles) {
                progressiveSource[file.filepath.replace('src/', '')] = {
                    code: file.code,
                };
            }
        } catch {
            //console.error('Error processing initial project files:', err);
        }

        const instructions = parsedMessage.payload.instructions;
        nextMessages = instructions ? [`instructions: ${instructions}`] : [];
        const taskList = parsedMessage.payload.taskList;
        if (taskList) {
            for (let i = 0; i < taskList.length; i++) {
                nextMessages = [
                    ...nextMessages,
                    `${taskList[i].filepath ? `file: File ${taskList[i].filepath}` : ''}`,
                    `taskInstruction: ${taskList[i].instruction || ''}`,
                    `code:${taskList[i].code || ''}`,
                ];
                if (i > 0) {
                    const replaceItem = taskList[i - 1];
                    const replaceItemPath = taskList[i - 1].filepath.replace('src/', '');
                    progressiveSource[replaceItemPath] = { code: replaceItem.code };
                }
            }
        }

        return { nextMessages, progressiveSource };
    }
};
const preprocessSessionMessages = (sessionMessages: any[]) => {
    const messagesGroupedByChatSessionId = sessionMessages.reduce(
        (acc, message) => {
            if (message.createdAt) {
                message.createdAt = new Date(message.createdAt).getTime();
            }

            if (message.messageType === 'question' && message.message) {
                if (message.message.startsWith('An error has been detected in the project and requires fixing')) {
                    message.isAutoFix = true;
                }
            }

            const { id, pid } = message;
            if (pid === null) {
                acc[id] = [];
                acc[id].push(message);
            } else {
                if (!acc[pid]) {
                    acc[pid] = [];
                }
                acc[pid].push(message);
            }
            return acc;
        },
        {} as Record<number, any[]>
    );

    const message = Object.values(messagesGroupedByChatSessionId)
        .map((chatSessionGroup: any) =>
            // Make sure answer comes after question
            chatSessionGroup.sort((a: any, b: any) => {
                const typeA = a.messageType.toUpperCase();
                const typeB = b.messageType.toUpperCase();
                if (typeA > typeB) return -1;
                if (typeA < typeB) return 1;
                return 0;
            })
        )
        .sort((a: any, b: any) => {
            if (a.length === 0 || a.length === 1 || b.length === 0 || b.length === 1) return 0;
            return a[1].createdAt - b[1].createdAt;
        });

    return message;
};

const AppkitDeps = {
    react: '^18.3.1',
    'react-app-polyfill': '^3.0.0',
    'react-dom': '^18.3.1',
    'react-refresh': '^0.11.0',
    'react-router': '^6.29.0',
    'react-router-dom': '^6.29.0',
    'source-map-loader': '^3.0.0',
    html2canvas: '1.4.1',
    '@appkit4/react-components': '4.32.0',
    '@appkit4/styles': '4.11.0',
    '@faker-js/faker': '^9.7.0',
    gsap: '^3.14.2',
    recharts: '^2.15.3',
};

const MaterialUIDeps = {
    react: '^18.3.1',
    'react-app-polyfill': '^3.0.0',
    'react-dom': '^18.3.1',
    'react-refresh': '^0.11.0',
    'react-router': '^6.29.0',
    'react-router-dom': '^6.29.0',
    highcharts: '^12.5.0',
    'highcharts-react-official': '^3.2.3',
    'highcharts-more': '^0.1.7',
    'source-map-loader': '^3.0.0',
    html2canvas: '1.4.1',
    '@mui/material': '^5.17.1',
    '@mui/icons-material': '5.18.0',
    '@mui/x-date-pickers': '^8.25.0',
    '@mui/x-data-grid': '8.28.2',
    '@emotion/react': '^11.14.0',
    '@emotion/styled': '^11.14.1',
    '@faker-js/faker': '^9.7.0',
    dayjs: '1.11.13',
    'date-fns': '^3.6.0',
    gsap: '^3.14.2',
};

const FluentUIDeps = {
    react: '^18.3.1',
    'react-app-polyfill': '^3.0.0',
    'react-dom': '^18.3.1',
    'react-refresh': '^0.11.0',
    'react-router': '^6.29.0',
    'react-router-dom': '^6.29.0',
    'source-map-loader': '^3.0.0',
    highcharts: '^12.5.0',
    'highcharts-react-official': '^3.2.3',
    'highcharts-more': '^0.1.7',
    html2canvas: '1.4.1',
    '@fluentui/react-components': '9.72.9',
    '@faker-js/faker': '^9.7.0',
    gsap: '^3.14.2',
};

const NewEraDeps = {
    react: '^18.3.1',
    'react-app-polyfill': '^3.0.0',
    'react-dom': '^18.3.1',
    'react-refresh': '^0.11.0',
    'react-router': '^6.29.0',
    'react-router-dom': '^6.29.0',
    'source-map-loader': '^3.0.0',
    html2canvas: '1.4.1',
    '@appkit4/react-components': '4.32.0',
    '@appkit4/styles': '4.11.0',
    '@appkit4/react-text-editor': '4.6.0',
    '@appkit4/ckeditor5-appkit-build': '4.4.2',
    '@faker-js/faker': '^9.7.0',
    gsap: '^3.14.2',
    recharts: '^2.15.3',
};
export type DesignSystems = 'Appkit' | 'Fluent' | 'MaterialUI' | 'NewEra' | 'AppkitRebrand';
export type TemplateVersion = 'v1' | 'v2';

const DepsMap: Record<DesignSystems, Record<string, string>> = {
    Appkit: AppkitDeps,
    Fluent: FluentUIDeps,
    MaterialUI: MaterialUIDeps,
    NewEra: NewEraDeps,
    AppkitRebrand: AppkitDeps,
};

const DEMO_USER: Record<string, string | number | string[]> = {
    given_name: 'Genaro',
    auth_time: 1737441228791,
    name: 'Genaro Rodriguez (MX)',
    exp: 0,
    family_name: 'Rodriguez',
    email: 'genaro.rodriguez@pwc.com',
    pwcUserType: 'RC_Internal',
    roles: ['ADMIN'],
};

const checkEnv = () => {
    let env = '';
    if (window.location.href.indexOf('localhost') > -1) {
        env = 'local';
    } else if (window.location.href.indexOf('https://designdraft.dev.pwc.com/') > -1) {
        env = 'dev';
    } else if (window.location.href.indexOf('https://designdraft.qa.pwc.com/') > -1) {
        env = 'qa';
    } else if (window.location.href.indexOf('https://designdraft.stage.pwc.com/') > -1) {
        env = 'stage';
    } else if (window.location.href.indexOf('https://designdraft.pwc.com/') > -1) {
        env = 'prod';
    }
    return env;
};

/**
 * Sets the document title with environment prefix for non-production environments.
 * - dev, qa, stage: "[env] Design Draft"
 * - prod (or unmatched): "Design Draft"
 */
const setDocumentTitle = () => {
    const env = checkEnv();
    const lowerEnvs = ['dev', 'qa', 'stage'];

    if (lowerEnvs.includes(env)) {
        document.title = `[${env.toUpperCase()}] Design Draft`;
    } else {
        document.title = 'Design Draft';
    }
};

const getCookie = (name: string) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
};

const deleteCookie = (name: string) => {
    if (getCookie(name)) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
};

/**
 * Clears all authentication-related data from the browser.
 * This includes localStorage, sessionStorage, and cookies.
 *
 * Use this before initiating SSO login to ensure a clean state,
 * especially to fix issues where cached auth data causes login failures.
 */
const clearAllAuthState = (): void => {
    // Clear auth-related localStorage items
    const authLocalStorageKeys = ['isUserLoggedIn', 'idToken', 'ads.loggedInUser'];
    authLocalStorageKeys.forEach((key) => localStorage.removeItem(key));

    // Clear all sessionStorage
    sessionStorage.clear();

    // Explicitly clear known auth cookies first
    const knownAuthCookies = ['XSRF-TOKEN', 'idToken', 'AppKit-Services', 'JSESSIONID'];
    const paths = ['/', '/rapidcraft', '/rapidcraft/v2', '/rapidcraft-service'];
    const hostname = window.location.hostname;

    knownAuthCookies.forEach((name) => {
        paths.forEach((path) => {
            // Without domain
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
            // With domain
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${hostname}`;
            // With leading dot domain (for subdomain cookies)
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${hostname}`;
        });
    });

    // Clear ALL cookies for current domain
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        if (!name) continue;

        paths.forEach((path) => {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${hostname}`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${hostname}`;
        });
    }

    console.log('[Auth] Cleared all authentication state');
    console.log('[Auth] Remaining cookies:', document.cookie);
};

export {
    deleteCookie,
    checkEnv,
    setDocumentTitle,
    getCodeFiles,
    tryToCompleteFinalJSON,
    tryToCompleteJSON,
    responseMessageParser,
    preprocessSessionMessages,
    DepsMap,
    DEMO_USER,
    clearAllAuthState,
};
